import { create } from 'zustand';

// ============================================================================
// formStore.ts — АТОМАРНОЕ СОСТОЯНИЕ ЗНАЧЕНИЙ ФОРМЫ (luna5, единый источник).
//
// Значения формы живут в ОДНОМ сторе (zustand) по ключу formId. Любой компонент
// (поле, пикер, подчинённый список) ЧИТАЕТ из стора (подписка) и ПИШЕТ в стор —
// БЕЗ хрупкой проп-цепочки AutoObjectForm→CustomLayoutRenderer→FormFieldRenderer
// →AddressField (она рвалась: onChange доходил, setValues вызывался, а value не
// обновлялось у существующей записи). Стор — единый источник для рендера И
// сохранения; изменения видны всем подписчикам (наблюдаемость).
// ============================================================================

/** Маркер НОВОЙ (ещё не сохранённой) записи в ключе значений формы. */
export const NEW_RECORD_FORM_ID = 'new';

/** ЕДИНЫЙ конструктор ключа значений формы: `тип.объект.recordId` для
 *  существующей записи, `тип.объект.new` — для новой. Привязка значений к
 *  ЗАПИСИ — по построению: значения одной записи не видны другой. */
export function buildFormKey(type: string, objectName: string, recordId?: string): string {
  return `${type}.${objectName}.${recordId || NEW_RECORD_FORM_ID}`;
}

/** true — ключ формы НОВОЙ записи (маркер 'new' в последнем сегменте). */
export function isNewRecordFormId(formId: string): boolean {
  const idx = formId.lastIndexOf('.');
  return idx >= 0 && formId.slice(idx + 1) === NEW_RECORD_FORM_ID;
}

interface FormState {
  values: Record<string, Record<string, unknown>>;
  /** Формы, уже инициализированные данными с сервера (защита от повторной
   *  инициализации: load/onOpen НЕ затирают пользовательский ввод). */
  initialized: Record<string, boolean>;
  markInitialized: (formId: string) => void;
  /** Запись одного поля формы. */
  setValue: (formId: string, attribute: string, value: unknown) => void;
  /** Полная установка значений формы (загрузка записи/onOpen) или
   *  функциональное обновление (как setState(prev=>...)). */
  setForm: (
    formId: string,
    values: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>),
  ) => void;
  /** Завершение СЕАНСА формы (закрытие): снимает флаг инициализации; для НОВОЙ
   *  записи дополнительно очищает черновик значений (следующее создание —
   *  с чистого листа), для существующей — значения сохраняются (повторное
   *  открытие не теряет несохранённый ввод). */
  endSession: (formId: string) => void;
  /** Очистить форму (закрытие/сброс). */
  reset: (formId: string) => void;
}

export const useFormStore = create<FormState>((set) => ({
  values: {},
  initialized: {},

  markInitialized: (formId) =>
    set((s) => ({ initialized: { ...s.initialized, [formId]: true } })),

  setValue: (formId, attribute, value) =>
    set((s) => ({
      values: {
        ...s.values,
        [formId]: { ...(s.values[formId] || {}), [attribute]: value },
      },
    })),

  setForm: (formId, values) =>
    set((s) => {
      const prev = s.values[formId] || {};
      const next = typeof values === 'function' ? (values as any)(prev) : values;
      return { values: { ...s.values, [formId]: { ...next } } };
    }),

  endSession: (formId) =>
    set((s) => {
      const init = { ...s.initialized };
      delete init[formId];
      // НОВАЯ запись: черновик закрывается вместе с формой — следующее
      // создание стартует чисто (defaults/prefill), остатки не протекают.
      if (!isNewRecordFormId(formId)) return { initialized: init };
      const values = { ...s.values };
      delete values[formId];
      return { values, initialized: init };
    }),

  reset: (formId) =>
    set((s) => {
      const next = { ...s.values };
      delete next[formId];
      const init = { ...s.initialized };
      delete init[formId];
      return { values: next, initialized: init };
    }),
}));

/** Чтение ОДНОГО поля формы из стора (подписка; стабильный selector — без
 *  бесконечных ререндеров). Форма (рендер полей) читает значения ТОЛЬКО так. */
export function useFormValue(formId: string, attribute: string): unknown {
  return useFormStore((s) => s.values[formId]?.[attribute]);
}

/** Чтение всех значений формы (подписка на объект формы целиком). */
export function useFormValues(formId: string): Record<string, unknown> {
  return useFormStore((s) => s.values[formId] ?? EMPTY_FORM_VALUES_STORE);
}

/** Стабильный пустой объект (см. EMPTY в AutoObjectForm — не пересоздавать). */
const EMPTY_FORM_VALUES_STORE: Record<string, unknown> = {};
